import React, { useCallback, useState } from 'react';
import { isSameDay } from 'date-fns';
import { CardView } from 'design-system/CardView';
import { SettingsItem } from 'design-system/SettingsItem';
import { getCategories } from 'actions';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import Modal from 'utils/packages/Modal';
import { selectOrderedCategories } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackRoute, } from 'utils/types/navigationV6';
import useStyles from 'hooks/useStyles';
import { useTimeout } from 'hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { rem } from 'design-system';
import { IconBoldCalendar, IconNote, IconText } from 'design-system/icons';
import IconThreeUser from 'design-system/icons/IconThreeUser';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import IconTag from 'features/feed/components/search/icons/toolbar/IconTag';
import { isWeb } from '../../../constants';
import IconTransactionCategory from '../icons/IconTransactionCategory';
const useAdvancedEditTransactions = (navigation, isEditInSearch, goBackKey) => {
    const route = useMainStackRoute();
    const keyBack = goBackKey || route.key;
    const categories = useAppSelector(selectOrderedCategories);
    const styles = useStyles(styleSet);
    const [isEditing, setIsEditing] = useState(!!route.params?.isEditing);
    const [selected, setSelected] = useState([]);
    const [selectedObj, setSelectedObj] = useState({});
    const [toastVisible, setToastVisible] = useState(false);
    const lastCategoryEditToastTime = useAppSelector((state) => state.deviceSettings.lastCategoryEditToastTime);
    const transactionsArray = useAppSelector((state) => state.transactions.transactionsArray);
    useTimeout(() => {
        setToastVisible(false);
    }, toastVisible ? 4000 : undefined // 4 seconds
    );
    const dispatch = useAppDispatch();
    const toSuccess = useCallback((editData) => {
        navigation.navigateDeprecated('LearningScreen', {
            keyBack,
            editData,
            transactionsCount: selected.length,
        });
    }, [keyBack, selected.length]);
    const onEdit = useCallback((isEditing) => {
        navigation.setParams({
            isEditing,
        });
        setIsEditing(isEditing);
        setSelected([]);
        setSelectedObj({});
    }, []);
    const onEditFinished = useCallback((callback) => {
        // This can run from an async save callback, by which point the owning
        // screen may have been popped off the stack. Dispatching SET_PARAMS to a
        // route that no longer exists logs an unhandled navigation action, so only
        // reset the param while the route is still mounted.
        const routeStillMounted = navigation
            .getState()
            ?.routes.some((r) => r.key === route.key);
        if (routeStillMounted) {
            navigation.setParams({ isEditing: false });
        }
        callback?.();
        setIsEditing(false);
        setSelected([]);
        setSelectedObj({});
    }, [navigation, route.key]);
    const onSelected = useCallback((data) => {
        setSelected((state) => {
            let array = [...state];
            const index = array.findIndex((item) => item.id === data.id);
            if (index === -1) {
                array.push({
                    id: data.id,
                    currentCategoryId: 'category' in data
                        ? data.category.id
                        : data.categoryId,
                });
            }
            else {
                array = array.filter((item) => item.id !== data.id);
            }
            return array;
        });
        setSelectedObj((prevSelectedObj) => {
            if (prevSelectedObj[data.id]) {
                return {
                    ...prevSelectedObj,
                    [data.id]: undefined,
                };
            }
            return {
                ...prevSelectedObj,
                [data.id]: true,
            };
        });
    }, []);
    const selectAllTransactions = useCallback((transactions) => {
        const allSelected = transactions.map((transaction) => ({
            id: transaction.id,
            currentCategoryId: 'category' in transaction
                ? transaction.category.id
                : transaction.categoryId,
        }));
        const allSelectedObj = transactions.reduce((acc, transaction) => {
            acc[transaction.id] = true;
            return acc;
        }, {});
        setSelected(allSelected);
        setSelectedObj(allSelectedObj);
    }, []);
    const deselectAllTransactions = useCallback(() => {
        setSelected([]);
        setSelectedObj({});
    }, []);
    const onEditCategories = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        Modal.hide(() => {
            navigation.navigateDeprecated('EditCategory', {
                multipleEditing: true,
                selected,
                onEditCallBack: (newCategory) => {
                    onEditFinished(() => {
                        if (isEditInSearch || selected.length > 1) {
                            toSuccess({ editType: 'categorise', category: newCategory });
                        }
                        else if (selected.length === 1) {
                            // Show toast for single transaction edits outside of search
                            const now = new Date();
                            const lastShown = lastCategoryEditToastTime
                                ? new Date(lastCategoryEditToastTime)
                                : null;
                            // Only show toast if it hasn’t been shown today
                            if (!lastShown || !isSameDay(now, lastShown)) {
                                setToastVisible(true);
                                dispatch(setDeviceSettingsKey('lastCategoryEditToastTime', now.toISOString()));
                            }
                        }
                    });
                },
            });
        });
        if (categories && !categories.length)
            dispatch(getCategories());
    }, [
        categories,
        selected,
        onEditFinished,
        isEditInSearch,
        toSuccess,
        lastCategoryEditToastTime,
    ]), 'advancedTransactionsEditing', false, true);
    const onEditTags = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        Modal.hide(() => {
            navigation.navigateDeprecated('AddTags', {
                type: 'multi',
                ids: selected.map((item) => item.id),
                onEditFinished: (tags) => onEditFinished(() => toSuccess({ editType: 'tags', tags })),
            });
        });
    }, [onEditFinished, selected, toSuccess]), 'advancedTransactionsEditing', false, true);
    const onAddToGroup = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        Modal.hide(() => {
            navigation.navigateDeprecated('AddToGroup', {
                transactions: transactionsArray.filter((t) => !!selectedObj[t.id]),
                addTransactionCallback: (groupName) => onEditFinished(() => toSuccess({ editType: 'group', groupName })),
            });
        });
    }, [onEditFinished, selectedObj, toSuccess, transactionsArray]), 'advancedTransactionsEditing', false, true);
    const onRename = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        Modal.hide(() => {
            navigation.navigateDeprecated('RenameTransaction', {
                keyBack,
                ids: selected.map(({ id }) => id),
                onSave: () => onEditFinished(),
            });
        });
    }, [keyBack, onEditFinished, selected]), 'advancedTransactionsEditing', false, true);
    const onEditDate = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        Modal.hide(() => {
            navigation.navigateDeprecated('EditDate', {
                keyBack,
                ids: selected.map(({ id }) => id),
                onSave: () => onEditFinished(),
            });
        });
    }, [keyBack, onEditFinished, selected]), 'advancedTransactionsEditing', false, true);
    const onAddNote = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        Modal.hide(() => {
            navigation.navigateDeprecated('AddNote', {
                keyBack,
                ids: selected.map(({ id }) => id),
                onSaveCallback: () => onEditFinished(),
            });
        });
    }, [keyBack, onEditFinished, selected]), 'advancedTransactionsEditing', false, true);
    const openEditModal = useCallback(() => {
        Modal.show(<CardView noMargin webPadding style={styles.card}>
        <SettingsItem light={isWeb} iconSize={44} title="Rename" onPress={onRename} icon={<IconText gradientKey="darkPurple"/>}/>
        <SettingsItem light={isWeb} iconSize={44} title="Date" onPress={onEditDate} icon={<IconBoldCalendar gradientKey="skyBlue"/>}/>
        <SettingsItem light={isWeb} iconSize={44} title="Category" onPress={onEditCategories} icon={<IconTransactionCategory category={{ id: 'bills' }}/>}/>
        <SettingsItem title="Tags" light={isWeb} iconSize={44} icon={<IconTag gradientKey="ultimate"/>} onPress={onEditTags}/>
        <SettingsItem title="Add note" light={isWeb} iconSize={44} icon={<IconNote gradientKey="darkPink"/>} onPress={onAddNote}/>
        <SettingsItem title="Add to group" light={isWeb} iconSize={44} icon={<IconThreeUser gradientKey="orange"/>} onPress={onAddToGroup}/>
      </CardView>, {
            title: 'Edit',
            lightBg: true,
            titleBottomBorder: true,
            containerStyle: styles.modalContent,
        });
    }, [
        onAddNote,
        onAddToGroup,
        onEditCategories,
        onEditDate,
        onEditTags,
        onRename,
        styles.card,
        styles.modalContent,
    ]);
    return {
        isEditing,
        selected,
        selectedLen: selected.length,
        openEditModal,
        selectedObj,
        onEdit,
        onSelected,
        selectAllTransactions,
        deselectAllTransactions,
        toastVisible,
        setToastVisible,
    };
};
const styleSet = createStyleSheets((colors) => ({
    modalContent: {
        ...(!isWeb && {
            paddingHorizontal: 0,
        }),
        ...(isWeb && {
            marginTop: rem(16),
            gap: rem(16),
        }),
    },
    card: {
        backgroundColor: isWeb ? colors.background.light : 'transparent',
    },
}));
export default useAdvancedEditTransactions;
